import React, { useMemo, useCallback } from "react";
import { useSelector } from 'react-redux';
import { differenceInDays } from "date-fns";
import {
    useTheme,
    Box,
    Card,
    CardActionArea,
    CardContent,
    Typography,
    Chip,
    Divider
} from "@mui/material";
import { getDateFromDateTime, formatTZDate } from "state/helpers";
import { hasPermission } from 'state/checkPermission';
import { boxStyles as styles } from 'components/CustomStyles';
import FlexBetween from "components/FlexBetween";
import FlexCenter from 'components/FlexCenter';


const BREATHING_GLOW = {
    "0%, 100%": { transform: "scale(1)", boxShadow: "0 0 0px rgba(255, 215, 0, 0.25)" },
    "50%": { transform: "scale(1.02)", boxShadow: "0 0 4px 2px rgba(255, 235, 100, 0.6)" },
};

const addDaySuffix = (time, dayDiff) => {
    if (!time) return 'N/A';
    if (dayDiff === 1) return `${time}+`;
    if (dayDiff > 1) return `${time}+${dayDiff}`;
    return time;
};

const CrewRow = React.memo(({ member, index }) => {
    const { palette } = useTheme();
    return (
        <Box
            display="flex"
            alignItems="center"
            alignContent="flex-start"
            gap={1}
            sx={{ color: member.gender === 'FM' ? palette.secondary.main : palette.primary.dark }}
        >
            <Typography width="18px" textAlign="right">{index + 1}.</Typography>
            <Typography variant="body2" sx={{ textAlign: 'right', whiteSpace: 'nowrap', width: '30px' }}>
                {member.crewCode}
            </Typography>
            <Typography variant="body2" sx={{ textAlign: 'left' }}>{member.crewName}</Typography>
            <Typography variant="body2" sx={{ textAlign: 'center' }}>
                ({member.post} | {member.base}{member.trnDuty && ` | ${member.trnDuty}`})
            </Typography>
        </Box>
    );
});

const FlightRow = React.memo(({ flight, timeMode, minCrewRouteTAT }) => {
    const { palette } = useTheme();

    const rawAtd = timeMode === 'UTC'
        ? (flight.fullAtd || flight.fullEtd)
        : (flight.fullAtdLocalBase || flight.fullEtdLocalBase);
    const fullAtd = rawAtd ? formatTZDate(rawAtd, "UTC", "ddMMMyy") : 'N/A';

    const td = timeMode === 'UTC' ? (flight.atd || flight.etd) : (flight.atdLocalBase || flight.etdLocalBase);
    const ta = timeMode === 'UTC' ? (flight.ata || flight.eta) : (flight.ataLocalBase || flight.etaLocalBase);

    const isWarning = flight.diffRegScheduleTat && flight.diffRegScheduleTat < minCrewRouteTAT;

    return (
        <Box
            display="flex"
            alignItems="center"
            gap={1}
            sx={{ color: isWarning ? palette.warning.main : 'inherit' }}
        >
            <Typography 
                variant="body2" 
                sx={{ 
                    textAlign: 'right', 
                    whiteSpace: 'nowrap', 
                    width: '30px' 
                }}
            >
                {flight.flightNo}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ textAlign: 'center', width: '55px' }}
            >
                {flight.flightReg || flight.acType}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ width: '45px' }}
            >
                {fullAtd}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ textAlign: 'center', width: '33px' }}
            >
                {td}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ textAlign: 'center', width: '25px' }}
            >
                {flight.dep}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ textAlign: 'center', width: '25px' }}
            >
                {flight.arr}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ textAlign: 'center', width: '33px' }}
            >
                {ta}
            </Typography>
            <Typography 
                variant="body2" 
                sx={{ textAlign: 'left', width: '10px' }}
            >
                {flight.pax}
            </Typography>
        </Box>
    );
});

const RouteCard = React.memo(({ 
    idx, 
    route, 
    timeMode, 
    minCrewRouteTAT, 
    canView, 
    onView 
}) => {
    const { palette } = useTheme();

    const routeDate = useMemo(() => {
        const dt = timeMode === 'UTC'
            ? (route.fullAtd || route.fullEtd)
            : (route.fullAtdLocalBase || route.fullEtdLocalBase);
        return getDateFromDateTime(dt);
    }, [route, timeMode]);

    const fullDT = timeMode === 'UTC'
        ? (route.fullAtd || route.fullEtd)
        : (route.fullAtdLocalBase || route.fullEtdLocalBase);
    const fullAT = timeMode === 'UTC'
        ? (route.fullAta || route.fullEta)
        : (route.fullAtaLocalBase || route.fullEtaLocalBase);

    const hasWarningRoute = useMemo(() =>
        route.routeDetails?.some((f) =>
            f.diffRegScheduleTat !== null && f.diffRegScheduleTat < minCrewRouteTAT
        ),
        [route.routeDetails, minCrewRouteTAT]
    );

    const sortedCrewMembers = useMemo(() =>
        [...(route.crewMembers || [])].sort((a, b) => a.rankPriority - b.rankPriority),
        [route.crewMembers]
    );

    const getTime = useCallback((time) => {
        if (!time) return 'N/A';
        const date = getDateFromDateTime(time);
        if (!date || isNaN(new Date(date))) return 'N/A';
        const dayDiff = differenceInDays(date, routeDate);
        try {
            return addDaySuffix(formatTZDate(time, 'UTC', 'HH:mm'), dayDiff);
        } catch {
            return 'N/A';
        }
    }, [routeDate]);

    const handleClick = useCallback(() => {
        if (canView) onView(route);
    }, [canView, onView, route]);

    const crewRoute = route.crewRoute?.length > 23
        ? route.crewRoute.slice(0, 23) + "..."
        : route.crewRoute;
    const flightNo = route.flightNo?.length > 35
        ? route.flightNo.slice(0, 35) + "..."
        : route.flightNo;

    return (
        <Card variant="outlined" sx={{ borderColor: palette.neutral.deep }}>
            <CardActionArea
                onClick={handleClick}
                disableRipple={!canView}
                sx={{ cursor: canView ? 'pointer' : 'default' }}
            >
            <CardContent sx={{ p: 1.2, "&:last-child": { pb: 1.2 } }}>
            <Box sx={{
                animation: hasWarningRoute ? "breathingGlow 3s ease-in-out infinite" : "none",
                "@keyframes breathingGlow": BREATHING_GLOW,
            }}>
                <FlexBetween>
                    <Box display="flex" alignItems="center" gap={1}>
                        <Chip
                            label={idx + 1}
                            variant="outlined"
                            color={hasWarningRoute ? "warning" : "primary"}
                            size="small"
                            sx={{ fontSize: "0.75rem", borderWidth: 1.5, height: 20, "& .MuiChip-label": { px: 0.8 } }}
                        />
                        <Typography variant="subtitle2" sx={{ lineHeight: 1 }}>
                            {routeDate ? formatTZDate(routeDate, 'UTC', "dd/MM/yy") : "N/A"}
                        </Typography>
                        <Typography
                            variant="subtitle2"
                            title={route.crewRoute}
                            sx={{ lineHeight: 1, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                            {crewRoute || ""}
                        </Typography>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                        {route.acType || ""} | {route.flightReg?.split("-")[1] || ""}
                    </Typography>
                </FlexBetween>

                <FlexBetween>
                    <Typography variant="subtitle2">{getTime(fullDT)}</Typography>
                    <Typography
                        variant="subtitle2"
                        title={route.flightNo}
                        sx={{ lineHeight: 1, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {flightNo || ""}
                    </Typography>
                    <Typography variant="subtitle2">{getTime(fullAT)}</Typography>
                </FlexBetween>
            </Box>

            <Divider sx={{ my: 0.5 }} />

            {route.routeDetails.map((flight, index) => (
                <FlightRow
                    key={index}
                    flight={flight}
                    timeMode={timeMode}
                    minCrewRouteTAT={minCrewRouteTAT}
                />
            ))}

            {sortedCrewMembers.length > 0 && (
                <>
                    <Divider sx={{ my: 0.5 }} />
                    {sortedCrewMembers.map((member, index) => (
                        <CrewRow key={index} member={member} index={index} />
                    ))}
                </>
            )}
            </CardContent>
            </CardActionArea>
        </Card>
    );
});

const PairingList = ({ routes, onView }) => {
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const params = useSelector((state) => state.params);

    const minCrewRouteTAT = useMemo(() =>
        Number(params?.find((item) => item.param === "MINIMUM_CREW_ROUTE_TAT_MINUTES")?.value) || 80,
        [params]
    );

    const canView = useMemo(() =>
        hasPermission(user?.permissions, "CrewPairings", "view", false),
        [user?.permissions]
    );

    if (!routes?.length)
        return (
        <FlexCenter sx={{ ...styles }}>
            <Typography color="text.secondary">No routes available</Typography>
        </FlexCenter>
    )


    return (
        <Box 
            display="flex" 
            flexDirection="column" 
            gap={2}
        >
            {routes.map((route, idx) => (
                <RouteCard
                    key={route._id || idx}
                    idx={idx}
                    route={route}
                    timeMode={timeMode}
                    minCrewRouteTAT={minCrewRouteTAT}
                    canView={canView}
                    onView={onView}
                />
            ))}
        </Box>
    );
};

export default PairingList;
